import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import {
	getImportWorkQueue,
	type WaitingImportRun,
	type WorkQueueSource,
} from "@/features/catalog/services/import-api.service";
import { hasPermission } from "@/lib/permissions";

/** Why a gated source can never come due, in the operator's words. */
const GATE_REASON: Record<string, string> = {
	origin_restricted: "refused at the origin",
	challenge: "behind a bot challenge",
};

const RUN_STATE: Record<WaitingImportRun["status"], string> = {
	staged: "Not resolved yet",
	resolved: "Resolved, not committed",
};

/**
 * The dashboard's import work (issue #255): the runs an operator started and
 * has not finished, the sources due for a refresh, and the ones gated so they
 * never will be.
 *
 * The work queue costs `admin:catalog:import` while the dashboard costs only
 * `read`. Without it the sections say so and ask for nothing: a request that
 * will be refused would render as a failure, and sections that silently vanish
 * make the screen look complete.
 */
export function ImportWorkSections() {
	const operator = useAdminAuthStore((state) => state.operator);
	const canImport = hasPermission(operator, "admin:catalog:import");

	const query = useQuery({
		queryKey: ["admin-catalog-work-queue"],
		queryFn: getImportWorkQueue,
		enabled: canImport,
		// Always stale, so coming back from committing a run refetches: nothing
		// on the import screens invalidates this key, and the app-wide 60s
		// staleTime would otherwise show that run as still waiting.
		staleTime: 0,
	});

	if (!canImport) {
		return (
			<Card className="rounded-3xl border-dashed bg-slate-50/60">
				<CardHeader>
					<CardTitle className="text-base">Import work</CardTitle>
					<CardDescription>
						The imports waiting and the sources due for a refresh need
						admin:catalog:import to see.
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	if (query.isPending) {
		return <Skeleton className="h-56 rounded-3xl" />;
	}

	if (query.isError) {
		// Unknown, not empty: "nothing waiting" is the reading an outage must
		// not produce.
		return (
			<Card className="rounded-3xl border-red-200 bg-red-50/60">
				<CardHeader>
					<CardTitle className="text-red-900">
						Could not load the import work
					</CardTitle>
					<CardDescription className="text-red-800">
						What is waiting is unknown right now, which is not the same as
						nothing. Work from the{" "}
						<Link className="underline" to="/catalog/imports">
							imports list
						</Link>{" "}
						until it comes back.
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	const { due, dueTotal, gated, waitingRuns, waitingTotal } = query.data;

	return (
		<div className="grid gap-4 lg:grid-cols-2">
			<WaitingRuns runs={waitingRuns} total={waitingTotal} />
			<div className="grid gap-4">
				<DueSources sources={due} total={dueTotal} />
				{gated.length > 0 ? <GatedSources sources={gated} /> : null}
			</div>
		</div>
	);
}

/** "N more" under a capped list, linking to the full, filtered list. */
function More({
	shown,
	total,
	what,
	to,
}: {
	shown: number;
	total: number;
	what: string;
	to: string;
}) {
	if (total <= shown) return null;
	return (
		<p className="mt-2 text-sm text-slate-600">
			{total - shown} more {what} than shown here.{" "}
			<Link className="underline" to={to}>
				See them all
			</Link>
		</p>
	);
}

function WaitingRuns({
	runs,
	total,
}: {
	runs: WaitingImportRun[];
	total: number;
}) {
	return (
		<Card className="rounded-3xl">
			<section aria-label="Imports waiting">
				<CardHeader>
					<CardTitle>Imports waiting</CardTitle>
					<CardDescription>
						Newest first: staged and not resolved, or resolved and not
						committed.{" "}
						<Link className="underline" to="/catalog/imports">
							Imports list
						</Link>
					</CardDescription>
				</CardHeader>
				<CardContent>
					{runs.length === 0 ? (
						<p className="text-sm text-slate-600">
							No import is waiting on anyone.
						</p>
					) : (
						<ul className="grid gap-2">
							{runs.map((run) => (
								<li key={run.id} className="rounded-2xl border p-3 text-sm">
									<Link
										className="font-medium underline"
										to={`/catalog/imports/${run.id}`}
									>
										{run.sourceName ?? "Unnamed source"}
									</Link>
									<span className="ml-2 text-slate-600">
										{RUN_STATE[run.status]}
									</span>
									<span className="mt-1 block text-slate-600">
										{run.rowsTotal} rows
										{run.rowsUnmatched > 0
											? `; ${run.rowsUnmatched} need a decision`
											: ""}
										{run.rowsNeedMapping > 0
											? `; ${run.rowsNeedMapping} held back`
											: ""}
									</span>
								</li>
							))}
						</ul>
					)}
					<More
						shown={runs.length}
						total={total}
						what="waiting"
						to="/catalog/imports?status=waiting"
					/>
				</CardContent>
			</section>
		</Card>
	);
}

function DueSources({
	sources,
	total,
}: {
	sources: WorkQueueSource[];
	total: number;
}) {
	return (
		<Card className="rounded-3xl">
			<section aria-label="Sources due for a refresh">
				<CardHeader>
					<CardTitle>Sources due for a refresh</CardTitle>
					<CardDescription>
						Oldest first; a source never harvested leads.{" "}
						<Link className="underline" to="/catalog/sources">
							Sources list
						</Link>
					</CardDescription>
				</CardHeader>
				<CardContent>
					{sources.length === 0 ? (
						<p className="text-sm text-slate-600">Nothing is due.</p>
					) : (
						<ul className="grid gap-1 text-sm">
							{sources.map((source) => (
								<li key={source.id}>
									<span className="font-medium">{source.name}</span>{" "}
									<span className="text-slate-600">
										{source.nextDueAt
											? `due since ${new Date(source.nextDueAt).toLocaleDateString()}`
											: "never harvested"}
									</span>
								</li>
							))}
						</ul>
					)}
					<More
						shown={sources.length}
						total={total}
						what="due"
						to="/catalog/sources?due=1"
					/>
				</CardContent>
			</section>
		</Card>
	);
}

function GatedSources({ sources }: { sources: WorkQueueSource[] }) {
	return (
		<Card className="rounded-3xl border-dashed">
			<section aria-label="Sources that cannot be fetched">
				<CardHeader>
					<CardTitle className="text-base">
						Sources that cannot be fetched
					</CardTitle>
					<CardDescription>
						Never due, because waiting does not change the answer. These need a
						conversation with the brand, not a retry.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ul className="grid gap-1 text-sm">
						{sources.map((source) => (
							<li key={source.id}>
								<span className="font-medium">{source.name}</span>{" "}
								<span className="text-slate-600">
									{GATE_REASON[source.gateType] ?? source.gateType}
								</span>
							</li>
						))}
					</ul>
				</CardContent>
			</section>
		</Card>
	);
}
